/**
 * 사유서 관리 페이지 (오너용)
 * 학부모가 신청한 예외 요청을 승인/거절합니다.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { formatExceptionTime } from '../../utils/exceptionTime';
import { tokenManager } from '../../infrastructure/api/tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getToken = () =>
  localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');

/**
 * 로그인 열쇠가 만료됐으면(401) 한 번 갱신하고 다시 부른다.
 * 🔴 2026-09-25 우장산 '사유서 숫자가 안 뜬다' — 이 화면은 맨 fetch 라 열쇠가 만료된 채
 *    화면을 열면(밤새 두었다가 아침에 여는 경우) 목록은 'API 오류: 401', 탭 숫자는 전부 빈 채로 남았다.
 *    열쇠는 부를 때마다 저장소에서 새로 읽는다(들고 있던 옛 열쇠를 쓰면 안 된다 — tokenManager 참고).
 *    401 은 서버가 요청을 처리하지 않았다는 뜻이라 승인·거절을 다시 보내도 두 번 처리되지 않는다.
 */
const authFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const call = () =>
    fetch(url, {
      ...init,
      headers: { ...((init.headers as Record<string, string>) || {}), Authorization: `Bearer ${getToken()}` },
    });
  const res = await call();
  if (res.status !== 401 || !tokenManager.getRefreshToken()) return res;
  try {
    await tokenManager.refreshAccessToken();
  } catch {
    return res;
  }
  return call();
};

type StatusFilter = 'pending' | 'parent_approved' | 'approved' | 'rejected' | 'cancelled' | 'all' | 'receipt_pending';

interface ExceptionRequest {
  id: string;
  student_id: string;
  student_name: string | null;
  member_category?: string | null; // 회원 구분(고등학생/N수생/성인 수험생 등)
  exception_date: string;
  exception_type: string | null;
  expected_arrival_time: string | null;
  expected_departure_time: string | null;
  reason: string | null;
  status: string;
  submitter_role?: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  parent_reviewed_at?: string | null;
  school_override?: boolean;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  batch_id?: string | null; // 연속 기간 일괄 제출 묶음 (단건은 null)
  penalty_exempt?: boolean | null;      // 승인 시 '벌점 미부과'였는지
  receipt_required?: boolean | null;    // 영수증을 받아야 하는 사유서인지
  receipt_checked_at?: string | null;   // 영수증 확인 시각 (null = 아직 안 냄)
  status_history?: Array<{
    at: string;
    action: string;
    status: string;
    actor_id?: string | null;
    actor_role?: string;
    note?: string | null;
    auto_cancelled_penalties?: number;
    restored_penalties?: number;
    school_override?: boolean;
  }> | null;
}

/** 화면에 한 줄로 보이는 단위. 연속 기간 사유서는 여러 건이 한 줄로 묶인다. */
interface DisplayRow {
  key: string;
  rep: ExceptionRequest;      // 대표 건 (가장 이른 날짜)
  items: ExceptionRequest[];
  batchId: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  submitted: '학생/학부모 제출',
  parent_approve: '학부모 승인',
  parent_reject: '학부모 거절',
  owner_approve: '운영자 승인',
  owner_override_approve: '운영자 강제 승인',
  owner_reject: '운영자 거절',
  owner_revoke: '승인 취소 → 거절로 전환',
  receipt_required_on: '영수증 받을 건으로 표시',
  receipt_required_off: '영수증 표시 해제',
  receipt_checked: '영수증 확인 → 벌점 면제',
  receipt_check_undo: '영수증 확인 취소',
  student_cancel: '학생이 취소',
  parent_cancel: '학부모가 취소',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '학생제출(부모승인 대기)',
  parent_approved: '부모승인됨(독서실 승인 대기)',
  approved: '최종 승인',
  rejected: '거절',
  cancelled: '신청자 취소',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  parent_approved: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-700',
};

const TAB_ITEMS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '부모승인 대기' },
  { key: 'parent_approved', label: '독서실 승인 대기' },
  { key: 'approved', label: '승인' },
  { key: 'rejected', label: '거절' },
  { key: 'receipt_pending', label: '🧾 영수증 미확인' },
  { key: 'cancelled', label: '🗂 취소 이력' },
];

const ExceptionRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<ExceptionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  // 탭 배지 — '아직 처리 안 된 것'의 개수. 개수만 세는 가벼운 조회를 쓴다
  // (탭마다 목록을 받아 세면 안 된다 — 사유서는 매장당 수백 건까지 쌓인다)
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [studentSearch, setStudentSearch] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null);
  const [detailRequest, setDetailRequest] = useState<ExceptionRequest | null>(null);
  // 승인 모달 (벌점 미부과 토글 + 메모)
  const [approveTarget, setApproveTarget] = useState<DisplayRow | null>(null);
  const [approveAction, setApproveAction] = useState<'approve' | 'override-approve'>('approve');
  const [approveExempt, setApproveExempt] = useState(false);
  const [approveReceipt, setApproveReceipt] = useState(false);  // 나중에 영수증을 받아야 하는 건
  const [approveNote, setApproveNote] = useState('');
  const [showTypeGuide, setShowTypeGuide] = useState(false);  // 사유 종류 안내 펼침

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await authFetch(
        `${API_BASE_URL}/v1/owner/schedule-exceptions/requests?status=${statusFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error(`API 오류: ${res.status}`);
      }
      const data = await res.json();
      setRequests(data);
    } catch (err: any) {
      setError(err.message || '데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // 탭 배지 개수 — 목록을 새로 받을 때마다 같이 갱신한다.
  // 승인·거절을 처리하면 숫자가 바로 줄어야 원장이 남은 일을 안다.
  const fetchTabCounts = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/v1/owner/schedule-exceptions/pending-count`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      setTabCounts({
        pending: Number(d?.pending ?? 0),
        parent_approved: Number(d?.parent_approved ?? 0),
        receipt_pending: Number(d?.receipt_pending ?? 0),
      });
    } catch {
      // 배지는 부가 정보다 — 실패해도 목록은 그대로 보여준다
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchTabCounts();
  }, [fetchRequests, fetchTabCounts]);

  // 승인·거절·영수증 처리 뒤에는 목록과 숫자를 함께 다시 받는다.
  // 🔴 2026-09-24 — 종전엔 목록만 다시 받아 탭 숫자가 처리 전 값으로 남았고,
  //    사이드바 숫자도 1분 폴링을 기다려야 해서 원장이 '숫자가 안 맞는다'고 봤다(우장산점).
  const reloadAfterAction = useCallback(async () => {
    await Promise.all([fetchRequests(), fetchTabCounts()]);
    window.dispatchEvent(new Event('exception-counts-changed'));
  }, [fetchRequests, fetchTabCounts]);

  const callOwnerAction = async (id: string, action: 'approve' | 'reject' | 'override-approve', note?: string, penaltyExempt?: boolean) => {
    const token = getToken();
    const res = await authFetch(
      `${API_BASE_URL}/v1/owner/exception-requests/${id}/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: note || null, penalty_exempt: !!penaltyExempt }),
      }
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || `오류: ${res.status}`);
    }
  };

  // 연속 기간 묶음 일괄 처리 — 처리 가능한 건만 서버가 처리하고 나머지는 건너뛴다
  const callBatchAction = async (
    batchId: string,
    action: 'approve' | 'reject' | 'override-approve',
    note?: string,
    penaltyExempt?: boolean
  ): Promise<{ processed_count: number; skipped: any[] }> => {
    const token = getToken();
    const res = await authFetch(
      `${API_BASE_URL}/v1/owner/exception-requests/batch/${batchId}/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: note || null, penalty_exempt: !!penaltyExempt }),
      }
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || `오류: ${res.status}`);
    }
    return res.json();
  };

  // 한 줄(단건 또는 묶음)에 대한 처리 — 묶음이면 묶음 API로 간다
  const runRowAction = async (
    row: DisplayRow,
    action: 'approve' | 'reject' | 'override-approve',
    note?: string,
    penaltyExempt?: boolean
  ) => {
    if (row.batchId) {
      const result = await callBatchAction(row.batchId, action, note, penaltyExempt);
      const skipped = result.skipped?.length || 0;
      alert(
        `${result.processed_count}일치를 처리했습니다.` +
        (skipped > 0 ? `\n(이미 처리된 ${skipped}일은 건너뛰었습니다)` : '')
      );
    } else {
      await callOwnerAction(row.rep.id, action, note, penaltyExempt);
    }
  };

  // 영수증 확인 표시 — 원장만 가능. 사진은 받지 않고 확인했다는 체크만 남긴다.
  const callReceiptAction = async (
    id: string,
    action: 'receipt-required' | 'receipt-check',
    value: boolean
  ) => {
    const token = getToken();
    const res = await authFetch(
      `${API_BASE_URL}/v1/owner/exception-requests/${id}/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value, note: null }),
      }
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || `오류: ${res.status}`);
    }
    return res.json();
  };

  // 묶음이면 그 안의 승인된 건 전부에 같은 처리를 한다
  const runReceiptAction = async (
    row: DisplayRow,
    action: 'receipt-required' | 'receipt-check',
    value: boolean
  ) => {
    setActionLoading(row.key);
    try {
      const targets = action === 'receipt-check'
        ? row.items.filter((i) => i.status === 'approved')
        : row.items;
      for (const t of targets) {
        await callReceiptAction(t.id, action, value);
      }
      await reloadAfterAction();
    } catch (err: any) {
      alert(err.message || '처리에 실패했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  // 승인/강제승인 모달 열기 (벌점 미부과 여부는 모달에서 결정)
  const openApprove = (row: DisplayRow, action: 'approve' | 'override-approve') => {
    setApproveTarget(row);
    setApproveAction(action);
    setApproveExempt(false);
    setApproveReceipt(false);
    setApproveNote('');
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    setActionLoading(approveTarget.key);
    try {
      await runRowAction(approveTarget, approveAction, approveNote || undefined, approveExempt);
      // '벌점 미부과'를 체크하면 영수증 칸은 화면에서 숨는데 값은 남아 있었다 → 미부과일 땐 보내지 않는다
      if (approveReceipt && !approveExempt) {
        for (const t of approveTarget.items) {
          await callReceiptAction(t.id, 'receipt-required', true);
        }
      }
      setApproveTarget(null);
      await reloadAfterAction();
    } catch (err: any) {
      alert(err.message || '승인에 실패했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (row: DisplayRow) => {
    setActionLoading(row.key);
    try {
      await runRowAction(row, 'reject', rejectNote);
      setRejectingId(null);
      setRejectNote('');
      await reloadAfterAction();
    } catch (err: any) {
      alert(err.message || '거절에 실패했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  // ⭐ 승인 취소 — 자동 취소됐던 벌점/상점 복원 + status=rejected
  const handleRevoke = async (row: DisplayRow) => {
    const targets = row.items.filter((i) => i.status === 'approved');
    if (targets.length === 0) return;
    // 묶음(연속 기간) 사유서는 한 번에 여러 건이 처리된다 — 몇 건인지 분명히 알린다
    const scopeText = targets.length > 1
      ? `묶음으로 신청된 ${targets.length}건의 승인을 한 번에 취소하시겠습니까?\n\n` +
        `· 이 묶음에 속한 ${targets.length}건이 모두 처리됩니다. 다른 사유서는 영향받지 않습니다.\n`
      : '이 사유서의 승인을 취소하시겠습니까?\n\n· 이 사유서 1건만 처리됩니다.\n';
    if (!window.confirm(
      scopeText +
      '· 승인하실 때 자동 취소됐던 벌점·상점이 원래대로 복원됩니다.\n' +
      '· 사유서는 거절 상태로 바뀝니다.'
    )) return;
    setActionLoading(row.key);
    try {
      const token = getToken();
      let restored = 0;
      for (const t of targets) {
        const res = await authFetch(
          `${API_BASE_URL}/v1/owner/exception-requests/${t.id}/revoke`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ note: null }),
          }
        );
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `오류: ${res.status}`);
        }
        const data = await res.json().catch(() => ({}));
        restored += data?.restored_penalties ?? 0;
      }
      alert(`승인을 취소했습니다.\n자동 복원된 점수 기록: ${restored}건`);
      await reloadAfterAction();
    } catch (err: any) {
      alert(err.message || '승인 취소에 실패했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  // 학생 이름으로 클라이언트 필터
  const filteredRequests = React.useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((r) => (r.student_name || '').toLowerCase().includes(q));
  }, [requests, studentSearch]);

  // 같은 batch_id 를 가진 건들을 한 줄로 묶는다. 단건은 그대로 한 줄.
  const displayRows = React.useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    const batchIndex = new Map<string, DisplayRow>();
    for (const req of filteredRequests) {
      if (!req.batch_id) {
        rows.push({ key: req.id, rep: req, items: [req], batchId: null });
        continue;
      }
      const found = batchIndex.get(req.batch_id);
      if (found) {
        found.items.push(req);
        if (req.exception_date < found.rep.exception_date) found.rep = req;
      } else {
        const row: DisplayRow = {
          key: `batch:${req.batch_id}`,
          rep: req,
          items: [req],
          batchId: req.batch_id,
        };
        batchIndex.set(req.batch_id, row);
        rows.push(row);
      }
    }
    for (const row of rows) {
      if (row.items.length > 1) {
        row.items.sort((a, b) => a.exception_date.localeCompare(b.exception_date));
      }
    }
    return rows;
  }, [filteredRequests]);

  /** 취소된 건의 '누가·언제·왜'. 이력을 펼치지 않아도 한 줄로 보이게 하려고 뽑아 쓴다. */
  const cancelInfo = (req: ExceptionRequest) => {
    const marks = (req.status_history || []).filter(
      (h) => h.action === 'student_cancel' || h.action === 'parent_cancel',
    );
    if (marks.length === 0) return null;
    const last = marks[marks.length - 1];
    return {
      // 조사까지 붙여서 내보낸다 — '학부모'는 받침이 없어 '이'를 붙이면 '학부모이'가 된다.
      who: last.action === 'parent_cancel' ? '학부모가' : '학생이',
      at: last.at || null,
      note: last.note || null,
    };
  };

  const shortDate = (dateStr: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    return m ? `${Number(m[2])}/${Number(m[3])}` : dateStr;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // 🔴 반드시 한국시간으로 못박는다. 지정하지 않으면 보는 사람 PC 의 시간대를 따라가고,
  //    서버가 세계표준시로 내려준 값이 그대로 찍혀 9시간 어긋나 보인다(2026-09-01 제보).
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">사유서 관리</h1>

      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {/* 🔴 '처리해야 할 것'에만 숫자를 붙인다 (2026-09-10 대표 지시).
                   승인·거절·취소 이력은 지난 기록이라 숫자가 붙으면 오히려 헷갈린다. */}
            {tabCounts[tab.key] > 0 && (
              <span
                className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                  statusFilter === tab.key ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700'
                }`}
              >
                {tabCounts[tab.key] > 99 ? '99+' : tabCounts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 학생 이름 검색 */}
      <div className="mb-4 relative max-w-xs">
        <input
          type="text"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
          placeholder="🔍 학생 이름으로 검색"
          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {studentSearch && (
          <button
            type="button"
            onClick={() => setStudentSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
            title="검색어 지우기"
          >
            ✕
          </button>
        )}
      </div>

      {/* 사유 종류 안내 (참고 — 편집 아님) */}
      <div className="mb-5">
        <button
          onClick={() => setShowTypeGuide((v) => !v)}
          className="text-sm font-medium text-blue-700 hover:underline"
        >
          {showTypeGuide ? '▲ 사유 종류 안내 닫기' : '❓ 사유 종류 안내 — 어떤 종류가 있고 벌점은 몇 점?'}
        </button>
        {showTypeGuide && (
          <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-gray-700">
            <div className="font-semibold text-gray-800 mb-2">📋 학생·학부모가 제출할 수 있는 사유 종류 (4가지, 고정)</div>
            <ul className="space-y-1.5">
              <li>• <b>결석</b> — 그날 오지 않음. 벌점: <b className="text-red-600">무단 10점</b> / 사유서 승인 시 <b className="text-amber-600">5점</b></li>
              <li>• <b>지각</b> — 예정보다 늦게 도착 (도착 예정 시각 제출). 벌점 <b className="text-amber-600">3점</b></li>
              <li>• <b>조퇴</b> — 예정보다 일찍 퇴실 (퇴실 예정 시각 제출). 벌점 <b className="text-amber-600">3점</b></li>
              <li>• <b>외출</b> — 잠깐 나갔다 복귀 (외출·복귀 시각). 벌점 <b className="text-gray-600">없음</b></li>
            </ul>
            <div className="mt-3 pt-3 border-t border-blue-100 text-xs text-gray-600">
              💡 승인할 때 <b>"벌점 미부과"</b>를 체크하면 그 사유서는 벌점이 부과되지 않습니다 (예: 학업 사유). 메모로 사유를 남길 수 있어요.
            </div>
          </div>
        )}
      </div>

      {/* 취소 이력 탭 설명 — 처리할 일이 아니라 '있었던 일' 목록임을 분명히 한다 */}
      {statusFilter === 'cancelled' && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
          🗂 학생·학부모가 <b>스스로 물린</b> 사유서 목록입니다. 원장님이 처리하실 일은 없습니다.
          <span className="block mt-1 text-xs text-gray-500">
            잘못 낸 것을 물리고 같은 날짜로 다시 낸 경우가 대부분입니다. 다시 낸 건은 <b>승인 대기</b> 탭에 따로 올라옵니다.
            <br />
            학생·학부모는 <b>원장님이 승인하기 전까지만</b> 물릴 수 있습니다. 승인하신 뒤에는 원장님만 <b>승인 취소</b>로 되돌리실 수 있습니다.
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? null /* 불러오기 실패를 '대기 중인 사유서가 없습니다'로 보이게 하지 않는다 */
      : filteredRequests.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {studentSearch.trim()
            ? `"${studentSearch.trim()}" 학생의 사유서가 없습니다.`
            : statusFilter === 'pending'
            ? '대기 중인 사유서가 없습니다.'
            : statusFilter === 'receipt_pending'
            ? '영수증을 기다리는 사유서가 없습니다. (승인할 때 "나중에 영수증 받을 건"을 체크하면 여기에 모입니다)'
            : statusFilter === 'cancelled'
            ? '학생·학부모가 스스로 물린 사유서가 없습니다.'
            : '해당하는 사유서가 없습니다.'}
        </div>
      ) : (
        /* Table */
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  학생
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  날짜
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  유형
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사유
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  신청일
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayRows.map((row) => {
                const req = row.rep;
                const isBatch = row.items.length > 1;
                const rowKey = row.key;
                const busy = actionLoading === rowKey;
                const canApprove = row.items.some((i) => i.status === 'parent_approved');
                const canOverride = row.items.some((i) => i.status === 'pending');
                const canAct = row.items.some(
                  (i) => i.status !== 'approved' && i.status !== 'rejected' && i.status !== 'cancelled',
                );
                const revokeCount = row.items.filter((i) => i.status === 'approved').length;
                const canRevoke = revokeCount > 0;
                // 영수증: 묶음이면 한 건이라도 표시돼 있으면 표시, 전부 확인돼야 확인 완료
                const receiptRequired = row.items.some((i) => !!i.receipt_required);
                const receiptChecked = receiptRequired
                  && row.items.filter((i) => i.receipt_required).every((i) => !!i.receipt_checked_at);
                const dateText = isBatch
                  ? `${shortDate(row.items[0].exception_date)}~${shortDate(row.items[row.items.length - 1].exception_date)}`
                  : formatDate(req.exception_date);
                // 묶음 안에서 상태가 갈릴 수 있다(일부만 처리됨) → 상태별로 몇 건인지 보여준다
                const statusCounts = new Map<string, number>();
                row.items.forEach((i) => statusCounts.set(i.status, (statusCounts.get(i.status) || 0) + 1));
                return (
                <React.Fragment key={rowKey}>
                  <tr className="hover:bg-gray-50 group">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      <button
                        type="button"
                        onClick={() => setDetailRequest(req)}
                        className="text-blue-700 hover:underline cursor-pointer"
                        title="자세히 보기"
                      >
                        {req.student_name || '-'}
                      </button>
                      {req.member_category && (
                        <span
                          className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold align-middle ${
                            /성인|전문직/.test(req.member_category)
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                          title="회원 구분 (성인이면 학부모 승인 없이 강제 승인 가능)"
                        >
                          {req.member_category}
                        </span>
                      )}
                      {req.requested_by_name && (
                        <div className="text-xs text-gray-400">
                          신청: {req.requested_by_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {dateText}
                      {isBatch && (
                        <span
                          className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-semibold align-middle"
                          title={row.items.map((i) => i.exception_date).join(', ')}
                        >
                          {row.items.length}일 묶음
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {req.exception_type || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                      {req.reason || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(req.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-start">
                        {Array.from(statusCounts.entries()).map(([st, cnt]) => (
                          <span
                            key={st}
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              STATUS_COLORS[st] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {STATUS_LABELS[st] || st}
                            {isBatch && ` ${cnt}일`}
                          </span>
                        ))}
                      </div>
                      {receiptRequired && (
                        <div className="mt-1">
                          {receiptChecked ? (
                            <span className="inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-800">
                              🧾 영수증 확인됨
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800">
                              🧾 영수증 미제출
                            </span>
                          )}
                        </div>
                      )}
                      {req.review_note && (
                        <div className="text-xs text-gray-400 mt-1">
                          {req.review_note}
                        </div>
                      )}
                      {(() => {
                        const c = row.items.some((i) => i.status === 'cancelled') ? cancelInfo(req) : null;
                        if (!c) return null;
                        return (
                          <div className="text-xs text-gray-500 mt-1">
                            {c.who} 물림{c.at ? ` · ${formatDateTime(c.at)}` : ''}
                            {c.note ? ` · ${c.note}` : ''}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm sticky right-0 bg-white z-10 border-l border-gray-200 group-hover:bg-gray-50">
                      <div className="flex flex-col space-y-1">
                        {/* ─── 대기 중인 건에 대한 액션 (승인/거절) ─── */}
                        {canAct && (
                          <>
                            <div className="flex space-x-2 flex-wrap gap-y-1">
                              {canApprove && (
                                <button
                                  onClick={() => openApprove(row, 'approve')}
                                  disabled={busy}
                                  className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                                >
                                  {busy ? '...' : (isBatch ? '전체 승인' : '승인')}
                                </button>
                              )}
                              {canOverride && (
                                <button
                                  onClick={() => openApprove(row, 'override-approve')}
                                  disabled={busy}
                                  title="부모 응답이 늦을 때 부모 승인 없이 강제 승인"
                                  className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
                                >
                                  {busy ? '...' : (isBatch ? '전체 강제승인' : '강제승인')}
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  setRejectingId(rejectingId === rowKey ? null : rowKey)
                                }
                                disabled={busy}
                                className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                              >
                                {isBatch ? '전체 거절' : '거절'}
                              </button>
                            </div>
                            {canOverride && (
                              <span className="text-[10px] text-gray-400">
                                부모 승인 대기중 · 학생: {req.submitter_role === 'student' ? '제출자' : ''}
                              </span>
                            )}
                            {req.school_override && (
                              <span className="text-[10px] text-purple-600">⚠ 강제승인</span>
                            )}
                          </>
                        )}
                        {/* ─── 승인된 건에 대한 액션 (승인 취소 등) ─── */}
                        {/* canAct와 canRevoke는 독립: 묶음에 승인+대기가 섞여도 양쪽이 모두 보인다 */}
                        {!canAct && (
                          <span className="text-gray-400 text-xs">
                            {req.reviewed_at ? formatDateTime(req.reviewed_at) : '-'}
                          </span>
                        )}
                        {canRevoke && !receiptRequired && (
                          <button
                            onClick={() => runReceiptAction(row, 'receipt-required', true)}
                            disabled={busy}
                            title="병원 사유서 등 — 나중에 영수증을 받아야 하는 건으로 표시합니다"
                            className="self-start px-2 py-0.5 text-[11px] bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50"
                          >
                            {busy ? '...' : '🧾 영수증 받을 건'}
                          </button>
                        )}
                        {canRevoke && receiptRequired && !receiptChecked && (
                          <button
                            onClick={() => runReceiptAction(row, 'receipt-check', true)}
                            disabled={busy}
                            title="영수증을 확인했으면 체크하세요. 벌점이 면제되고 이미 부과된 자동벌점은 취소됩니다"
                            className="self-start px-2 py-0.5 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {busy ? '...' : '✅ 영수증 확인함'}
                          </button>
                        )}
                        {/* 2026-10-02 송파점: 승인 창에서 '영수증 받을 건'을 잘못 체크하면 되돌릴 버튼이 없었다.
                            '승인 취소'는 거절로 바뀌어 벌점이 오히려 무거워지므로 따로 둔다. 벌점은 건드리지 않는다. */}
                        {canRevoke && receiptRequired && !receiptChecked && (
                          <button
                            onClick={() => runReceiptAction(row, 'receipt-required', false)}
                            disabled={busy}
                            title="잘못 체크했을 때: 영수증 받을 건 표시를 지웁니다. 벌점은 바뀌지 않습니다"
                            className="self-start px-2 py-0.5 text-[11px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            {busy ? '...' : '↩ 영수증 표시 해제'}
                          </button>
                        )}
                        {canRevoke && receiptChecked && (
                          <button
                            onClick={() => runReceiptAction(row, 'receipt-check', false)}
                            disabled={busy}
                            title="잘못 체크했을 때: 면제를 되돌리고 취소했던 벌점을 복원합니다"
                            className="self-start px-2 py-0.5 text-[11px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            {busy ? '...' : '↩ 영수증 확인 취소'}
                          </button>
                        )}
                        {canRevoke && (
                          <button
                            onClick={() => handleRevoke(row)}
                            disabled={busy}
                            title="승인을 취소하고 거절로 되돌립니다. 승인 때 자동 취소됐던 벌점·상점이 복원됩니다"
                            className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded hover:bg-orange-600 disabled:opacity-50 transition-colors"
                          >
                            {busy ? '...' : revokeCount > 1 ? `↩ 승인 취소 (${revokeCount}건)` : '↩ 승인 취소'}
                          </button>
                        )}
                      </div>
                      {/* 이력 보기 토글 — 항상 노출 (이력 있을 때만) */}
                      {req.status_history && req.status_history.length > 0 && (
                        <button
                          onClick={() => setHistoryOpenId(historyOpenId === rowKey ? null : rowKey)}
                          className="mt-1 text-[11px] text-blue-600 hover:underline"
                        >
                          {historyOpenId === rowKey ? '📜 이력 숨기기' : `📜 이력 보기 (${req.status_history.length}건)`}
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* 이력 타임라인 행 */}
                  {historyOpenId === rowKey && req.status_history && req.status_history.length > 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 bg-blue-50">
                        {isBatch && (
                          <div className="text-xs text-gray-600 mb-2">
                            ※ 연속 기간 묶음입니다. 아래 이력은 <b>{formatDate(req.exception_date)}</b> 건 기준이며,
                            같은 묶음의 나머지 날짜도 같은 흐름으로 처리됩니다.
                          </div>
                        )}
                        <div className="text-xs font-medium text-gray-700 mb-2">📜 상태 변경 이력 (시간순)</div>
                        <ol className="space-y-1.5 text-xs">
                          {req.status_history.map((h, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-gray-500 whitespace-nowrap">
                                {h.at ? formatDateTime(h.at) : '-'}
                              </span>
                              <span className="text-gray-400">·</span>
                              <span className="font-medium text-gray-900">
                                {ACTION_LABELS[h.action] || h.action}
                              </span>
                              {h.actor_role && (
                                <span className="text-[10px] text-gray-500">({h.actor_role})</span>
                              )}
                              {h.note && (
                                <span className="text-gray-600">— {h.note}</span>
                              )}
                              {h.auto_cancelled_penalties !== undefined && h.auto_cancelled_penalties > 0 && (
                                <span className="text-orange-700">· 자동취소 {h.auto_cancelled_penalties}건</span>
                              )}
                              {h.restored_penalties !== undefined && h.restored_penalties > 0 && (
                                <span className="text-green-700">· 복원 {h.restored_penalties}건</span>
                              )}
                              {h.school_override && (
                                <span className="text-purple-700">· 강제승인</span>
                              )}
                            </li>
                          ))}
                        </ol>
                      </td>
                    </tr>
                  )}
                  {/* Reject note input row */}
                  {rejectingId === rowKey && (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 bg-red-50">
                        {isBatch && (
                          <div className="text-xs text-red-700 mb-2">
                            ⚠ {dateText} <b>{row.items.length}일치를 한 번에 거절</b>합니다.
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="거절 사유를 입력하세요 (선택)"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleReject(row);
                            }}
                          />
                          <button
                            onClick={() => handleReject(row)}
                            disabled={busy}
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                          >
                            {busy ? '처리중...' : '거절 확인'}
                          </button>
                          <button
                            onClick={() => {
                              setRejectingId(null);
                              setRejectNote('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
                          >
                            취소
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 사유서 상세 모달 */}
      {/* 승인 모달 — 벌점 미부과 토글 + 메모 */}
      {approveTarget && (() => {
        const t = approveTarget.rep;
        const tItems = approveTarget.items;
        const tIsBatch = tItems.length > 1;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setApproveTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {approveAction === 'override-approve' ? '강제 승인' : '사유서 승인'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {t.student_name || '-'}
                {t.member_category && (
                  <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    /성인|전문직/.test(t.member_category) ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {t.member_category}
                  </span>
                )}
                {' · '}
                {tIsBatch
                  ? `${shortDate(tItems[0].exception_date)}~${shortDate(tItems[tItems.length - 1].exception_date)} (${tItems.length}일)`
                  : formatDate(t.exception_date)}
                {' · '}{t.exception_type || '-'}
              </p>
            </div>
            <div className="p-5 space-y-4 text-sm">
              {approveAction === 'override-approve' && (
                <p className="text-xs text-purple-700 bg-purple-50 rounded p-2">부모 승인 없이 강제 승인합니다. 부모와 직접 통화/연락 후에만 사용하세요.</p>
              )}
              {tIsBatch && (
                <p className="text-xs text-indigo-700 bg-indigo-50 rounded p-2">
                  연속 기간으로 제출된 <b>{tItems.length}일치를 한 번에 승인</b>합니다.
                  <span className="block mt-0.5 text-indigo-600">
                    {tItems.map((i) => i.exception_date).join(' · ')}
                  </span>
                </p>
              )}
              {/* 학생이 적은 사유 — 관리자가 읽고 '진짜 학습 사유인지' 판단 */}
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500 mb-1">📝 학생이 적은 사유</div>
                <div className="whitespace-pre-wrap text-gray-800">{t.reason || '(사유 없음)'}</div>
                {formatExceptionTime(t.exception_type, t.expected_arrival_time, t.expected_departure_time) && (
                  <div className="text-xs text-gray-500 mt-1.5">
                    {formatExceptionTime(t.exception_type, t.expected_arrival_time, t.expected_departure_time)}
                  </div>
                )}
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={approveExempt} onChange={(e) => setApproveExempt(e.target.checked)} className="mt-1" />
                <span>
                  <span className="font-medium text-gray-800">벌점 미부과</span>
                  <span className="block text-xs text-gray-500">사유를 확인하고 <b>진짜 학습 등 정당한 사유</b>면 체크하세요. 체크 시 이 사유서엔 벌점 미부과. 미체크 시 정책대로 부과 — 사유결석 5점 · 지각/조퇴 3점. (메모에 판단 근거를 남기세요)</span>
                </span>
              </label>
              {!approveExempt && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={approveReceipt} onChange={(e) => setApproveReceipt(e.target.checked)} className="mt-1" />
                  <span>
                    <span className="font-medium text-gray-800">나중에 영수증 받을 건</span>
                    <span className="block text-xs text-gray-500">병원 진료 등 — 지금은 정책대로 부과하고, 나중에 영수증을 확인하면 그때 벌점을 면제합니다. 아직 안 낸 학생은 <b>🧾 영수증 미확인</b> 탭에서 한 번에 볼 수 있어요.</span>
                  </span>
                </label>
              )}
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  메모 {approveExempt && <span className="text-gray-400">(미부과 사유 권장)</span>}
                </label>
                <textarea value={approveNote} onChange={(e) => setApproveNote(e.target.value)} rows={2}
                  placeholder={approveExempt ? '예: 학업(모의고사 응시)로 벌점 미부과' : '메모 (선택)'}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setApproveTarget(null)} className="px-4 py-2 rounded-lg border text-sm">취소</button>
              <button onClick={confirmApprove} disabled={actionLoading === approveTarget.key}
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                {actionLoading === approveTarget.key
                  ? '처리 중...'
                  : (approveExempt ? '미부과로 승인' : (tIsBatch ? `${tItems.length}일 전체 승인` : '승인'))}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {detailRequest && (() => {
        const r = detailRequest;
        // 외출 사유서: 시각 순서로 외출/복귀 자동 정렬
        let outTime: string | null = null;
        let backTime: string | null = null;
        if (r.exception_type === '외출') {
          const a = r.expected_arrival_time || null;
          const d = r.expected_departure_time || null;
          if (a && d) {
            [outTime, backTime] = a <= d ? [a, d] : [d, a];
          } else {
            outTime = a || d;
          }
        }
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setDetailRequest(null)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="px-5 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">사유서 상세</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-800'}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                  {r.school_override && (
                    <span className="text-[11px] text-purple-700">⚠ 강제승인</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setDetailRequest(null)}
                  className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                >
                  ✕
                </button>
              </div>

              {/* 본문 */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
                {/* 기본 정보 */}
                <section>
                  <h4 className="font-semibold text-gray-700 mb-2">📌 기본 정보</h4>
                  <dl className="grid grid-cols-3 gap-y-1.5 gap-x-3 text-gray-800">
                    <dt className="text-gray-500">학생</dt>
                    <dd className="col-span-2 font-medium">{r.student_name || '-'}</dd>
                    <dt className="text-gray-500">날짜</dt>
                    <dd className="col-span-2">{formatDate(r.exception_date)}</dd>
                    <dt className="text-gray-500">사유 종류</dt>
                    <dd className="col-span-2">{r.exception_type || '-'}</dd>
                  </dl>
                </section>

                {/* 시간 정보 */}
                {(r.exception_type === '외출' || r.exception_type === '지각' || r.exception_type === '조퇴') && (
                  <section>
                    <h4 className="font-semibold text-gray-700 mb-2">🕐 시간 정보</h4>
                    <dl className="grid grid-cols-3 gap-y-1.5 gap-x-3 text-gray-800">
                      {r.exception_type === '외출' && (
                        <>
                          <dt className="text-gray-500">외출 시각</dt>
                          <dd className="col-span-2 font-medium">{outTime || '-'}</dd>
                          <dt className="text-gray-500">복귀 시각</dt>
                          <dd className="col-span-2 font-medium">{backTime || '-'}</dd>
                        </>
                      )}
                      {r.exception_type === '지각' && (
                        <>
                          <dt className="text-gray-500">도착 예정</dt>
                          <dd className="col-span-2 font-medium">{r.expected_arrival_time || '-'}</dd>
                        </>
                      )}
                      {r.exception_type === '조퇴' && (
                        <>
                          <dt className="text-gray-500">퇴실 예정</dt>
                          <dd className="col-span-2 font-medium">{r.expected_departure_time || '-'}</dd>
                        </>
                      )}
                    </dl>
                  </section>
                )}

                {/* 사유 */}
                <section>
                  <h4 className="font-semibold text-gray-700 mb-2">📝 사유</h4>
                  <p className="bg-gray-50 rounded p-3 whitespace-pre-wrap text-gray-800">
                    {r.reason || '(사유 없음)'}
                  </p>
                </section>

                {/* 제출 정보 */}
                <section>
                  <h4 className="font-semibold text-gray-700 mb-2">📨 제출 정보</h4>
                  <dl className="grid grid-cols-3 gap-y-1.5 gap-x-3 text-gray-800">
                    <dt className="text-gray-500">제출자</dt>
                    <dd className="col-span-2">
                      {r.submitter_role === 'student' ? '학생' : r.submitter_role === 'parent' ? '학부모' : (r.requested_by_name || '-')}
                    </dd>
                    {r.requested_by_name && (
                      <>
                        <dt className="text-gray-500">신청자</dt>
                        <dd className="col-span-2">{r.requested_by_name}</dd>
                      </>
                    )}
                    <dt className="text-gray-500">제출 시각</dt>
                    <dd className="col-span-2">{formatDateTime(r.created_at)}</dd>
                    {r.parent_reviewed_at && (
                      <>
                        <dt className="text-gray-500">학부모 검토</dt>
                        <dd className="col-span-2">{formatDateTime(r.parent_reviewed_at)}</dd>
                      </>
                    )}
                  </dl>
                </section>

                {/* 검토 정보 */}
                {(r.reviewed_at || r.review_note) && (
                  <section>
                    <h4 className="font-semibold text-gray-700 mb-2">✍ 운영자 검토</h4>
                    <dl className="grid grid-cols-3 gap-y-1.5 gap-x-3 text-gray-800">
                      <dt className="text-gray-500">처리 시각</dt>
                      <dd className="col-span-2">{r.reviewed_at ? formatDateTime(r.reviewed_at) : '-'}</dd>
                      {r.review_note && (
                        <>
                          <dt className="text-gray-500">메모</dt>
                          <dd className="col-span-2 whitespace-pre-wrap">{r.review_note}</dd>
                        </>
                      )}
                    </dl>
                  </section>
                )}

                {/* 상태 변경 이력 */}
                {r.status_history && r.status_history.length > 0 && (
                  <section>
                    <h4 className="font-semibold text-gray-700 mb-2">📜 상태 변경 이력 (시간순)</h4>
                    <ol className="space-y-1.5 text-xs bg-blue-50 rounded p-3">
                      {r.status_history.map((h, idx) => (
                        <li key={idx} className="flex items-start gap-2 flex-wrap">
                          <span className="text-gray-500 whitespace-nowrap">{h.at ? formatDateTime(h.at) : '-'}</span>
                          <span className="text-gray-400">·</span>
                          <span className="font-medium text-gray-900">{ACTION_LABELS[h.action] || h.action}</span>
                          {h.actor_role && (
                            <span className="text-[10px] text-gray-500">({h.actor_role})</span>
                          )}
                          {h.note && <span className="text-gray-600">— {h.note}</span>}
                          {h.auto_cancelled_penalties !== undefined && h.auto_cancelled_penalties > 0 && (
                            <span className="text-orange-700">· 자동취소 {h.auto_cancelled_penalties}건</span>
                          )}
                          {h.restored_penalties !== undefined && h.restored_penalties > 0 && (
                            <span className="text-green-700">· 복원 {h.restored_penalties}건</span>
                          )}
                          {h.school_override && <span className="text-purple-700">· 강제승인</span>}
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </div>

              {/* 푸터 */}
              <div className="px-5 py-3 border-t flex justify-end">
                <button
                  type="button"
                  onClick={() => setDetailRequest(null)}
                  className="px-4 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ExceptionRequestsPage;
